import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
  Grid,
  GridItem,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { addModel, updateModel } from '../../../../../redux/actions/model';
import { getSeries } from '../../../../../redux/actions/series';
import {
  renderFormControl,
  renderSelectFormControl,
} from 'utils/DynamicFormData';
import '../../../companyProfile/css.css';

// Yup Validation Schema
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters long'),
  color: Yup.string().required('Color is required'),
  ram: Yup.number().required('ram is required'),
  storage: Yup.number().required('storage is required'),
});

// const steps = [
//   { title: 'First', description: 'Enter Item Information' },
//   { title: 'Second', description: 'Review and Save' },
// ];

const ModelMasterForm = ({
  isOpen,
  onClose,
  isEditMode = false,
  currentRow = null,
  onSave,
}) => {
  const [row, setRow] = useState({
    name: '',
    color: '',
    ram: '',
    storage: '',
    ordinal: '',
    seriesid: '',
  });

  const [errors, setErrors] = useState({});
  // const [activeStep, setActiveStep] = useState(0);
  const toast = useToast();
  const textColor = useColorModeValue('gray.800', 'white');
  const dispatch = useDispatch();
  const seriesData = useSelector((state) => state.series.list);
  const [data, setData] = React.useState([...seriesData]);

  // Function to clear form data
  const clearFormData = () => {
    setRow({
      name: '',
      color: '',
      ram: '',
      storage: '',
      ordinal: '',
      seriesid: '',
    });
    setErrors({});
  };

  useEffect(() => {
    dispatch(getSeries());
  }, [dispatch]);

  useEffect(() => {
    setData([...seriesData]);
  }, [seriesData]);

  // Populate form with current row data when in edit mode
  useEffect(() => {
    if (!isEditMode) {
      clearFormData(); // Clear data when adding a new item
    } else if (isEditMode && currentRow) {
      setRow({
        name: currentRow.name || '',
        color: currentRow.color || '',
        ram: currentRow.ram || '',
        storage: currentRow.storage || '',
        ordinal: currentRow.ordinal || '',
        seriesid: currentRow.seriesid || '',
      });
    }
  }, [isEditMode, currentRow]);

  const validateStep = async () => {
    try {
      // Validate the whole row object instead of individual fields
      await validationSchema.validate(row, { abortEarly: false });

      setErrors({}); // Clear previous errors
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errorObj = {};
        err.inner.forEach((error) => {
          errorObj[error.path] = error.message;
        });
        setErrors(errorObj);
        return false;
      }
      return false;
    }
  };

  const handleInputChange = (field, value) => {
    setRow((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear the specific field error when user starts typing
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[field]) {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const onFormSubmit = async () => {
    const processedData = {
      ...row,
      ordinal: Number(row.ordinal),
      ram: Number(row.ram),
      storage: Number(row.storage),
      // rateperpurchaseid: Number(row.rateperpurchaseid)
    };

    try {
      if (isEditMode && currentRow && currentRow.id) {
        // Update existing item
        processedData.id = currentRow.id;
        await dispatch(updateModel(processedData));
      } else {
        // Add new item
        await dispatch(addModel(processedData));
      }

      // Close the modal
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error.message || 'An error occurred while saving the category.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateStep();
    if (isValid) {
      onFormSubmit(); // Call the form submit logic if validation is successful
    } else {
      toast({
        title: 'Validation Error',
        description: 'Please correct the errors before saving 30.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent
        bg={useColorModeValue('white', '#2d3748')}
        color={useColorModeValue('gray.800', 'white')}
        borderRadius="20px"
        p={5}
        boxShadow="lg"
      >
        <ModalHeader fontSize="36px" fontWeight="600" mb={4}>
          {isEditMode ? 'Edit Model' : 'Add New Model'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* <Stepper index={activeStep} colorScheme="teal">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <Box flexShrink="0">
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper> */}

          {/* {activeStep === 0 && ( */}
          <Grid templateColumns="repeat(2, 1fr)" mt={8}>
            <GridItem className="seperator-width">
              {renderFormControl(
                'name',
                'Name',
                {
                  value: row.name,
                  onChange: (e) => handleInputChange('name', e.target.value),
                },
                errors,
                textColor,
                'text',
                true, // optional: mark as required
              )}
            </GridItem>
            <GridItem className="seperator-width">
              {renderFormControl(
                'ordinal',
                'Ordinal',
                {
                  value: row.ordinal,
                  onChange: (e) => handleInputChange('ordinal', e.target.value),
                },
                errors,
                textColor,
                'number',
                true, // optional: mark as required
              )}
            </GridItem>
            <GridItem className="seperator-width">
              {renderFormControl(
                'color',
                'Color',
                {
                  value: row.color,
                  onChange: (e) => handleInputChange('color', e.target.value),
                },
                errors,
                textColor,
                'text',
                true, // optional: mark as required
              )}
            </GridItem>
            <GridItem className="seperator-width">
              {renderFormControl(
                'ram',
                'RAM',
                {
                  value: row.ram,
                  onChange: (e) => handleInputChange('ram', e.target.value),
                },
                errors,
                textColor,
                'number',
                true, // optional: mark as required
              )}
            </GridItem>
            <GridItem className="seperator-width">
              {renderFormControl(
                'storage',
                'Storage',
                {
                  value: row.storage,
                  onChange: (e) => handleInputChange('storage', e.target.value),
                },
                errors,
                textColor,
                'number',
                true, // optional: mark as required
              )}
            </GridItem>
            <GridItem className="seperator-width">
              {renderSelectFormControl(
                'seriesid',
                'Series',
                seriesData,
                {
                  value: row.seriesid,
                  onChange: (value) =>
                    handleInputChange('seriesid', value),
                },
                errors,
                textColor,
                true, // isRequired
                'Select Series', // placeholder
              )}
            </GridItem>
          </Grid>
          {/* )} */}

          {/* {activeStep === 1 && (
            <Box mt={8}>
              <strong>Name:</strong> {row.name}
              <br />
              <strong>storage:</strong> {row.storage}
              <br />
            </Box>
          )} */}
        </ModalBody>
        <ModalFooter>
          {/* {activeStep > 0 && (
            <Button
              onClick={handlePrevious}
              variant="ghost"
              colorScheme="teal"
              mr={3}
            >
              Previous
            </Button>
          )}

          {activeStep < steps.length - 1 && (
            <Button onClick={handleNext} colorScheme="teal">
              Next
            </Button>
          )} */}

          <Button onClick={handleSubmit} colorScheme="green" ml={3}>
            Save
          </Button>

          <Button variant="ghost" onClick={onClose} ml={3}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModelMasterForm;
